import { useEffect, useRef } from 'react';
import { createTextCanvas, toCanvasFontFamily } from '../utils/textureUtils';
import './TextEditor.css';

/* ── Types ── */
export interface TextEditorForm {
  text: string;
  fontFamily: string;
  fontSize: number;
  color: string;
  strokeColor: string;
  strokeWidth: number;
  rotation: number;
}

interface Props {
  form: TextEditorForm;
  fontOptions: string[];
  onChange: (field: string, value: string | number, silent?: boolean) => void;
  onColorPicker: (type: string) => void;
  onFontPicker: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  showFontPicker?: boolean;
  isLightColor?: (hex: string) => boolean;
}

/* ── Preview canvas ── */
function TextPreview({ form }: { form: TextEditorForm }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const src = createTextCanvas({
      text: form.text || ' ',
      fontFamily: form.fontFamily,
      fontSize: form.fontSize,
      color: form.color,
      strokeColor: form.strokeColor,
      strokeWidth: form.strokeWidth,
      canvasResolution: 1024,
    });
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(src, 0, 0, canvas.width, canvas.height);
  }, [form.text, form.fontFamily, form.fontSize, form.color, form.strokeColor, form.strokeWidth]);

  return (
    <div className="te-preview">
      <canvas ref={ref} width={512} height={512} className="te-preview__canvas" />
    </div>
  );
}

/* ── Step button (±) ── */
function Stepper({
  label, value, unit = '', min = 0, max = 800, step = 1,
  onChange,
}: {
  label: string; value: number; unit?: string; min?: number; max?: number; step?: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="te-row">
      <span className="te-row__label">{label}</span>
      <div className="te-stepper">
        <button type="button" className="te-stepper__btn"
          onClick={() => onChange(Math.max(min, value - step))}>−</button>
        <input
          type="number"
          className="te-stepper__input"
          value={value}
          min={min}
          max={max}
          onChange={e => {
            const v = Math.max(min, Math.min(max, Number(e.target.value) || min));
            onChange(v);
          }}
        />
        <button type="button" className="te-stepper__btn"
          onClick={() => onChange(Math.min(max, value + step))}>+</button>
        {unit && <span className="te-stepper__unit">{unit}</span>}
      </div>
    </div>
  );
}

/* ── Main component ── */
export default function TextEditor({
  form,
  fontOptions,
  onChange,
  onColorPicker,
  onFontPicker,
  onDelete,
  onDuplicate,
  showFontPicker = false,
  isLightColor,
}: Props) {
  const rotDeg = Math.round((form.rotation * 180) / Math.PI);

  return (
    <div className="te">

      {/* 1 ── Aperçu temps réel */}
      <TextPreview form={form} />

      {/* 2 ── Zone de texte */}
      <div className="te-section">
        <textarea
          className="te-textarea"
          rows={2}
          value={form.text}
          onChange={e => onChange('text', e.target.value)}
          placeholder="Votre texte…"
          spellCheck={false}
        />
      </div>

      {/* 3 ── Couleur + style */}
      <div className="te-section te-format-bar">
        <button
          type="button"
          className={`te-color-btn ${isLightColor?.(form.color) ? 'is-light' : ''}`}
          style={{ background: form.color }}
          onClick={() => onColorPicker('form-edit-color')}
          title="Couleur du texte"
        >
          <span className="te-color-label">A</span>
        </button>
        <div className="te-divider" />
        <button
          type="button"
          className={`te-color-btn te-color-btn--stroke ${isLightColor?.(form.strokeColor) ? 'is-light' : ''}`}
          style={{ background: form.strokeColor }}
          onClick={() => onColorPicker('form-edit-stroke')}
          title="Couleur du contour"
        >
          <span className="te-color-label" style={{ WebkitTextStroke: '1px currentColor', color: 'transparent' }}>A</span>
        </button>
      </div>

      {/* 4 ── Police */}
      <div className="te-section">
        <button type="button" className="te-font-row" onClick={onFontPicker}>
          <span className="te-font-row__name" style={{ fontFamily: toCanvasFontFamily(form.fontFamily) }}>
            {form.fontFamily}
          </span>
          <span className="te-font-row__arrow">{showFontPicker ? '›' : '›'}</span>
        </button>

        {showFontPicker && (
          <div className="te-font-grid">
            {fontOptions.map(f => (
              <button
                key={f}
                type="button"
                className={`te-font-option ${form.fontFamily === f ? 'is-active' : ''}`}
                style={{ fontFamily: toCanvasFontFamily(f) }}
                onClick={() => onChange('fontFamily', f)}
              >
                {f}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 5 ── Taille de la police */}
      <div className="te-section">
        <Stepper
          label="Taille de la police"
          value={form.fontSize}
          min={10}
          max={800}
          step={10}
          onChange={v => onChange('fontSize', v)}
        />
      </div>

      {/* 6 ── Contour */}
      <div className="te-section">
        <Stepper
          label="Épaisseur contour"
          value={form.strokeWidth}
          unit="px"
          min={0}
          max={40}
          step={1}
          onChange={v => onChange('strokeWidth', v, true)}
        />
      </div>

      {/* 7 ── Rotation */}
      <div className="te-section te-transform">
        <div className="te-row">
          <span className="te-row__label">Rotation</span>
          <div className="te-stepper">
            <button type="button" className="te-stepper__btn"
              onClick={() => onChange('rotation', form.rotation - (Math.PI / 180) * 5)}>←</button>
            <span className="te-stepper__display">{rotDeg}°</span>
            <button type="button" className="te-stepper__btn"
              onClick={() => onChange('rotation', form.rotation + (Math.PI / 180) * 5)}>→</button>
          </div>
        </div>
      </div>

      {/* 8 ── Actions */}
      <div className="te-section te-actions">
        {onDuplicate && (
          <button type="button" className="te-action-btn" onClick={onDuplicate}>
            <svg viewBox="0 0 20 20" fill="none"><rect x="7" y="7" width="9" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.6"/><path d="M13 7V5a1.5 1.5 0 0 0-1.5-1.5H5A1.5 1.5 0 0 0 3.5 5v6.5A1.5 1.5 0 0 0 5 13h2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
            Dupliquer
          </button>
        )}
        {onDelete && (
          <button type="button" className="te-action-btn te-action-btn--danger" onClick={onDelete}>
            <svg viewBox="0 0 20 20" fill="none"><path d="M5 7h10M8 7V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2m2 0v9a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Supprimer
          </button>
        )}
      </div>
    </div>
  );
}
